// src/pages/agency/AgencyEdit.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { useAtom } from 'jotai';
import { selectedInsuranceCompaniesAtom } from '../../store/agencyStore';
import AgencyHeader from '../../components/headers/AgencyHeader';
import AgencyRegistrationPop from '../../components/popups/AgencyRegistrationPop';
import { StatusConfirmationPopup } from '../../components/popups/AgencyDetailStopPop';
import AgencyEditConfirmationPop from '../../components/popups/AgencyEditConfirmationPop';
import AgencyInfoTab from '../../tabs/agencyEdit/AgencyInfoTab';
import AgencyInsuranceTab from '../../tabs/agencyEdit/AgencyInsuranceTab';
import AgencySolicitorsTab from '../../tabs/agencyDetail/AgencySolicitorsTab';
import AgencySolicitorsEditTab from '../../tabs/agencyEdit/AgencySolicitorsTab';
import { fetchDocumentTypes } from '../../services/api/DocumentTypeService';
import {
  fetchAgencyData,
  createAgency,
  updateAgency
} from '../../services/api/AgencyService';
import {
  fetchInsuranceCompanies,
  fetchAgencyInsuranceCompanies,
  groupCompaniesByKana
} from '../../services/api/InsuranceService';
import {
  fetchSolicitor,
  createSolicitor,
  searchSolicitor,
  updateSolicitor,
  importSolicitorsFromCSV,
  exportSolicitorsToCSV
} from '../../services/api/SolicitorService';
import { validateAgencyForm, displayAgencyValidationErrorsWithDialog } from '../../utils/agencyValidation';
import { validateSolicitorForm, displayValidationErrorsWithDialog } from '../../utils/solicitorValidation';
import {
  useErrorDialog,
  showSuccessDialog
} from '../../providers/ErrorDialogProvider';
import '../../styles/Tabs.css';
import '../../styles/AgencyRegistration.css';
import iconTitle from '../../assets/img/icon-square.svg';
import iconBtnUl from '../../assets/img/icon-ul.svg';
import iconBtnDl from '../../assets/img/icon-dl.svg';
import useRequireAuth from '../../hooks/useRequireAuth';
import { formatDateForApi } from '../../utils/dateFormatter';
import { buildDocumentTypeSelectionKeys, buildDocumentTypesForApi, normalizeDocumentTypes } from '../../utils/documentTypeUtils';

// 代理店編集ページコンポーネント
// ※pages/adminにも同様のコンポーネントがあるため、修正時は同時に確認すること
const AgencyEdit = () => {
  const { agencyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const solicitorId = queryParams.get('solicitorId');
  const { showError } = useErrorDialog();
  const userInfo = useRequireAuth();
  const canEditDocumentTypes = ! !userInfo?.is_admin;

  const showBackendError = (err, defaultMsg) => {
    const details = err.response?.data?.detail;
    let msg = defaultMsg;
    if (details) {
      if (Array.isArray(details)) {
        msg = details
          .map(d => (typeof d === 'string' ? d : d.msg || JSON.stringify(d)))
          .join('\n');
      } else if (typeof details === 'string') {
        msg = details;
      }
    }
    showError({
      message: msg,
      error: err,
      statusCode: err.response?.status
    });
  };

  // ステート定義
  const [agencyData, setAgencyData] = useState(null);
  const [agencyInsuranceCompanies, setAgencyInsuranceCompanies] = useState([]);
  const [solicitors, setSolicitors] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);

  const [insuranceCompanies, setInsuranceCompanies] = useState({});
  const [, setSelectedInsuranceCompanies] = useAtom(selectedInsuranceCompaniesAtom);
  const [formData, setFormData] = useState({
    tradingCode: '',
    name: '',
    nameKana: '',
    contractDate: '',
    cancellationDate: '',
    postalCode: '',
    prefecture: '',
    city: '',
    building: '',
    phoneNumber: '',
    accountname: '',
    documentTypeIds: [],
  });
  const [documentTypes, setDocumentTypes] = useState([]);

  // 募集人フォームの状態
  const [solicitorForm, setSolicitorForm] = useState({
    branchName: '',
    name: '',
    nameKana: '',
    startDate: '',
    endDate: '',
    profile: '',
    accountname: '',
    isActive: true,
  });

  // 停止／再開ポップアップ用
  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [statusConfirmationData, setStatusConfirmationData] = useState(null);

  // 登録・編集ポップアップ用
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [showEditConfirmation, setShowEditConfirmation] = useState(false);

  // 保険会社チェックボックス状態
  const [selectedCompanies, setSelectedCompanies] = useState({});

  const formatDate = dateString =>
    dateString ? format(parseISO(dateString), 'yyyy/MM/dd') : '';

  // 募集人ステータス変更ボタンハンドラ
  const handleStatusButtonClick = (flag) => {
    setStatusConfirmationData({
      id: solicitorId,
      name: solicitorForm.name,
      username: solicitorForm.accountname,
      isActive: solicitorForm.isActive,
      target: 'solicitor',
    });
    setShowStatusConfirmation(true);
  };

  // 新規登録確定
  const confirmRegistration = async () => {
    setIsLoading(true);
    let registrationSuccess = false;
    let newAgencyId = null;
    try {
      if (activeTab === 'info') {
        // 再バリデーション
        const validationResult = validateAgencyForm(formData);
        if (!displayAgencyValidationErrorsWithDialog(validationResult, showError)) {
          setIsLoading(false);
          return;
        }

        // 代理店登録
        const agencyPayload = {
          agency_name: formData.name,
          agency_name_kana: formData.nameKana,
          trading_code: formData.tradingCode,
          contract_date: formData.contractDate || null,
          cancellation_date: formData.cancellationDate || null,
          postal_code: formData.postalCode,
          prefecture: formData.prefecture,
          city: formData.city,
          building: formData.building,
          phone: formData.phoneNumber,
          accountname: formData.accountname
        };
        if (canEditDocumentTypes) {
          agencyPayload.document_types = buildDocumentTypesForApi(formData.documentTypeIds, documentTypes);
        }
        const response = await createAgency(agencyPayload);
        newAgencyId = response.id;
        registrationSuccess = true;

        // 成功メッセージを表示
        showSuccessDialog('代理店の登録が完了しました。');
      } else if (activeTab === 'solicitors') {
        // 再バリデーション
        const validationResult = validateSolicitorForm(solicitorForm);
        if (!displayValidationErrorsWithDialog(validationResult, showError)) {
          setIsLoading(false);
          return;
        }

        // 募集人登録
        const solicitorPayload = {
          branch_name: solicitorForm.branchName || null,
          solicitor_name: solicitorForm.name,
          solicitor_name_kana: solicitorForm.nameKana,
          agency_id: agencyId ? parseInt(agencyId) : null,
          accountname: solicitorForm.accountname,
          start_date: solicitorForm.startDate || null,
          end_date: solicitorForm.endDate || null,
          profile: solicitorForm.profile || null,
          is_active: solicitorForm.isActive,
        };
        await createSolicitor(solicitorPayload);
        registrationSuccess = true;

        // 成功メッセージを表示
        showSuccessDialog('募集人の登録が完了しました。');

        setSolicitorForm({
          branchName: '',
          name: '',
          nameKana: '',
          startDate: '',
          endDate: '',
          profile: '',
          accountname: '',
          isActive: true,
        });
      }
    } catch (err) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
      registrationSuccess = false;
    } finally {
      setIsLoading(false);
      setShowConfirmationPopup(false);
      if (registrationSuccess && activeTab === 'info' && newAgencyId) {
        navigate(`/agency/edit/${newAgencyId}`);
      }
    }
  };

  const handleDocumentTypeToggle = (documentTypeId) => {
    setFormData((prev) => {
      const current = new Set(prev.documentTypeIds || []);
      if (current.has(documentTypeId)) {
        current.delete(documentTypeId);
      } else {
        current.add(documentTypeId);
      }
      return {
        ...prev,
        documentTypeIds: Array.from(current)
      };
    });
  };

  // リトライ可能なエラーかどうかを判定する関数
  const isErrorRetryable = (error) => {
    // タイムアウトエラー
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return true;
    }

    // サーバーエラー（5xx系）
    const status = error.response?.status;
    if (status >= 500 && status < 600) {
      return true;
    }

    // 一時的な接続エラー
    if (status === 408 || status === 429 || status === 503 || status === 502) {
      return true;
    }

    // ネットワークエラー
    if (error.message?.includes('Network Error') || error.message?.includes('ECONNREFUSED')) {
      return true;
    }

    return false;
  };

  // 最終的なエラーハンドリング
  const handleFinalError = (err, status, attemptCount, maxAttempts) => {
    if (status === 403) {
      setError('編集できません。');
    } else if (isErrorRetryable(err)) {
      // リトライ上限に達したタイムアウトエラー
      setError(
        `サーバーとの通信がタイムアウトしました。\n` +
        `${maxAttempts}回試行しましたが接続できませんでした。\n` +
        `時間をおいて再度お試しいただくか、ページを再読み込みしてください。`
      );
    } else {
      // その他のエラー
      showBackendError(err, 'データの取得に失敗しました。');
    }
  };

  // 初期データロード（リトライ機能付き、API順序最適化）
  const fetchDataWithRetry = async () => {
    const maxRetries = 3;
    let currentAttempt = 0;

    // 申請処理直後かどうかを判定
    const isFromUpload = document.referrer.includes('/insurance/upload') ||
      location.state?.fromUpload;

    while (currentAttempt < maxRetries) {
      try {
        currentAttempt++;
        console.log(`データ取得開始 (試行 ${currentAttempt}/${maxRetries})`);

        setIsLoading(true);
        setError(null);
        setErrorStatus(null);

        if (!agencyId) {
          setError('代理店IDが指定されていません。');
          return;
        }

        // 申請処理直後の場合は段階的に待機
        if (isFromUpload) {
          const waitTime = currentAttempt * 2000; // 2秒、4秒、6秒と増加
          console.log(`申請処理後の遷移を検出。待機中... (${waitTime}ms)`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        } else if (currentAttempt > 1) {
          // 通常のリトライの場合は指数バックオフ
          const backoffTime = Math.min(1000 * Math.pow(2, currentAttempt - 1), 8000);
          console.log(`リトライ前の待機... (${backoffTime}ms)`);
          await new Promise(resolve => setTimeout(resolve, backoffTime));
        }

        // === API呼び出しを最適化された順序で実行 ===
        let companies, grouped, agency, solicitorData, links;

        // Step 1: 代理店基本情報取得（最重要、比較的軽量）
        console.log('Step 1: 代理店基本情報を取得中...');
        const agencyStart = Date.now();
        agency = await fetchAgencyData(agencyId, {
          showErrorDialog: false
        });
        console.log(`代理店情報取得完了: ${Date.now() - agencyStart}ms`);

        setAgencyData(agency);
        const documentTypeSelection = buildDocumentTypeSelectionKeys(
          agency.document_types || [],
          documentTypes
        );
        setFormData({
          tradingCode: agency.trading_code,
          name: agency.name,
          nameKana: agency.name_kana,
          contractDate: formatDate(agency.contract_date),
          cancellationDate: formatDate(agency.cancellation_date),
          postalCode: agency.postal_code,
          prefecture: agency.prefecture,
          city: agency.city,
          building: agency.building || '',
          phoneNumber: agency.phone,
          accountname: agency.accountname,
          documentTypeIds: documentTypeSelection
        });

        // Step 2: 保険会社一覧取得（キャッシュ可能、時間がかかる場合がある）
        console.log('Step 2: 保険会社一覧を取得中...');
        const companiesStart = Date.now();
        try {
          companies = await fetchInsuranceCompanies();
          grouped = groupCompaniesByKana(companies);
          setInsuranceCompanies(grouped);
          console.log(`保険会社一覧取得完了: ${Date.now() - companiesStart}ms`);
        } catch (error) {
          if (currentAttempt < maxRetries && isErrorRetryable(error)) {
            console.log(`保険会社一覧取得でタイムアウト。リトライで再試行します。`);
            throw error; // リトライロジックに委ねる
          } else {
            console.warn('保険会社一覧の取得に失敗しましたが、処理を続行します。', error.message);
            // 空のデータで続行
            grouped = {};
            setInsuranceCompanies({});
          }
        }

        // Step 3: 募集人情報取得
        if (solicitorId) {
          console.log('Step 3: 募集人編集情報を取得中...');
          const solicitorStart = Date.now();
          const sol = await fetchSolicitor(solicitorId);
          console.log(`募集人編集情報取得完了: ${Date.now() - solicitorStart}ms`);
          setSolicitorForm({
            branchName: sol.branch_name || '',
            name: sol.name,
            nameKana: sol.name_kana,
            startDate: formatDate(sol.start_date),
            endDate: formatDate(sol.end_date),
            profile: sol.profile || '',
            accountname: sol.accountname,
            isActive: sol.is_active,
          });
          setActiveTab('solicitors');
        } else {
          console.log('Step 3: 募集人一覧を取得中...');
          const solicitorsStart = Date.now();
          const search = await searchSolicitor({
            agency_id: parseInt(agencyId),
            target: null,
            search_word: null,
            is_active: null,
          });
          setSolicitors(search);
          console.log(`募集人一覧取得完了: ${Date.now() - solicitorsStart}ms`);
        }

        // Step 4: 紐づけ情報取得（最後に実行 - 申請処理との競合を回避）
        console.log('Step 4: 紐づけ情報を取得中... (申請処理完了待ち)');

        // 申請処理直後の場合は、追加で少し待機してから紐づけ情報を取得
        if (isFromUpload) {
          console.log('申請処理後のため、紐づけ情報取得前に追加待機...');
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        const linksStart = Date.now();
        try {
          links = await fetchAgencyInsuranceCompanies(agencyId);
          setAgencyInsuranceCompanies(links);
          console.log(`紐づけ情報取得完了: ${Date.now() - linksStart}ms`);

          // 保険会社データがある場合のみ選択状態を設定
          if (grouped && Object.keys(grouped).length > 0) {
            const linkedIds = links.map(l => l.id);
            setSelectedCompanies(linkedIds);
          }
        } catch (error) {
          if (currentAttempt < maxRetries && isErrorRetryable(error)) {
            console.log(`紐づけ情報取得でタイムアウト。リトライで再試行します。`);
            throw error; // リトライロジックに委ねる
          } else {
            console.warn('紐づけ情報の取得に失敗しましたが、基本機能は利用できます。', error.message);
            setAgencyInsuranceCompanies([]);
            setSelectedCompanies([]);
          }
        }

        console.log(`全データ取得成功 (試行 ${currentAttempt})`);
        break; // 成功したらループを抜ける

      } catch (err) {
        console.error(`データ取得エラー (試行 ${currentAttempt}/${maxRetries}):`, {
          message: err.message,
          code: err.code,
          status: err.response?.status,
          url: err.config?.url
        });

        const status = err.response?.status;
        setErrorStatus(status);

        // リトライ可能なエラーかどうかを判定
        const isRetryableError = isErrorRetryable(err);

        if (isRetryableError && currentAttempt < maxRetries) {
          console.log(`リトライ可能なエラーです。リトライします... (${currentAttempt + 1}/${maxRetries})`);
          // continue でループを継続
          continue;
        } else {
          // リトライ不可能なエラーまたはリトライ上限到達
          console.error(`最終エラー (試行 ${currentAttempt}/${maxRetries}):`, err);
          handleFinalError(err, status, currentAttempt, maxRetries);
          break;
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchDataWithRetry();
  }, [agencyId, solicitorId]);

  useEffect(() => {
    const fetchDocTypes = async () => {
      try {
        const response = await fetchDocumentTypes();
        setDocumentTypes(normalizeDocumentTypes(response));
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      }
    };

    fetchDocTypes();
  }, []);

  useEffect(() => {
    if (!agencyData || documentTypes.length === 0) {
      return;
    }
    setFormData((prev) => {
      if (prev.documentTypeIds?.length) {
        return prev;
      }
      const selection = buildDocumentTypeSelectionKeys(
        agencyData.document_types || [],
        documentTypes
      );
      return {
        ...prev,
        documentTypeIds: selection
      };
    });
  }, [agencyData, documentTypes]);

  // 手動リトライボタン用の関数
  const handleManualRetry = () => {
    console.log('手動リトライを実行...');
    fetchDataWithRetry();
  };

  // 確認後ステータス変更
  const confirmStatusChange = async () => {
    setIsLoading(true);
    try {
      if (statusConfirmationData?.target === 'solicitor') {
        const newStatus = !statusConfirmationData.isActive;
        await updateSolicitor(solicitorId, { is_active: newStatus });
        setSolicitorForm(prev => ({ ...prev, isActive: newStatus }));
        setSolicitors(prev =>
          prev.map(s =>
            s.id === solicitorId ? { ...s, isActive: newStatus } : s
          )
        );
      }
    } catch {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
    } finally {
      setIsLoading(false);
      setShowStatusConfirmation(false);
    }
  };

  // CSVアップロード用の関数
  const handleCsvUpload = (agencyId) => {
    // ファイル入力要素を作成
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';

    // ファイル選択時の処理
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        setIsLoading(true);

        // APIを呼び出して登録（Content-Typeはブラウザが自動的に設定するので指定しない）
        const response = await importSolicitorsFromCSV(agencyId, file);
        console.log('CSVアップロード成功:', response);

        // 成功メッセージの表示
        if (response && response.message) {
          showSuccessDialog(response.message);
        } else {
          showSuccessDialog('募集人情報のアップロードが完了しました。');
        }

        // 募集人データを再取得
        const searchData = {
          agency_id: parseInt(agencyId),
          target: null,
          search_word: null,
          is_active: null
        };
        const solicitorsResponse = await searchSolicitor(searchData);
        setSolicitors(solicitorsResponse);
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      } finally {
        setIsLoading(false);
      }
    };

    // ファイル選択ダイアログを開く
    fileInput.click();
  };

  // CSVダウンロード用の関数
  const handleCsvDownload = async (agencyId) => {
    try {
      await exportSolicitorsToCSV(agencyId);
      console.log('募集人CSVのダウンロードが完了しました');
    } catch (error) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
    }
  };

  // 編集確定
  const confirmEdit = async () => {
    setIsLoading(true);
    try {
      if (activeTab === 'info') {
        // 日付のフォーマットをハイフン形式からスラッシュ形式に変換
        formData.contractDate = formatDateForApi(formData.contractDate);
        formData.cancellationDate = formatDateForApi(formData.cancellationDate);
        const agencyUpdatePayload = {
          trading_code: formData.tradingCode,
          agency_name: formData.name,
          agency_name_kana: formData.nameKana,
          contract_date: formData.contractDate || null,
          cancellation_date: formData.cancellationDate || null,
          postal_code: formData.postalCode,
          prefecture: formData.prefecture,
          city: formData.city,
          building: formData.building,
          phone: formData.phoneNumber,
          accountname: formData.accountname
        };
        if (canEditDocumentTypes) {
          agencyUpdatePayload.document_types = buildDocumentTypesForApi(formData.documentTypeIds, documentTypes);
        }
        await updateAgency(agencyId, agencyUpdatePayload);
        await showSuccessDialog('代理店情報の編集が完了しました。');
        navigate(`/admin/agency/detail/${agencyId}`);
      } else if (activeTab === 'insurance') {
        // 代理店に紐づいている保険会社のIDセットを作成（初期データから取得）
        const linkedCompanyIds = new Set(agencyInsuranceCompanies.map(company => company.id));

        // 選択された保険会社をリストに変換
        const selectedCompaniesList = Object.entries(insuranceCompanies)
          .flatMap(([type, categories]) => (Object.entries(categories).flatMap(([category, companies]) => (companies))))
          .filter(company => selectedCompanies.includes(company.id)).map(company => ({
            name: company.name,
            id: company.id,
            type: company.type,
            isLinked: linkedCompanyIds.has(company.id) // 代理店との紐づき状態
          }));

        // Jotaiのアトムに保存
        setSelectedInsuranceCompanies(selectedCompaniesList);

        // 状態が確実に更新されてから遷移するために遅延させる
        setTimeout(() => {
          navigate(`/agency/insurance/upload/${agencyId}`);
        }, 100);

        return;
      } else if (activeTab === 'solicitors') {
        // 日付のフォーマットをハイフン形式からスラッシュ形式に変換
        solicitorForm.startDate = formatDateForApi(solicitorForm.startDate);
        solicitorForm.endDate = formatDateForApi(solicitorForm.endDate);
        // 募集人情報のバリデーション
        const validationResult = validateSolicitorForm(solicitorForm, !userInfo.is_agent || userInfo.is_admin);
        if (!displayValidationErrorsWithDialog(validationResult, showError)) {
          setIsLoading(false);
          setShowEditConfirmation(false);
          return;
        }

        const solicitorUpdateData = {
          branch_name: solicitorForm.branchName || null,
          solicitor_name: solicitorForm.name,
          solicitor_name_kana: solicitorForm.nameKana,
          agency_id: parseInt(agencyId),
          start_date: solicitorForm.startDate || null,
          end_date: solicitorForm.endDate || null,
          profile: solicitorForm.profile || null,
          is_active: solicitorForm.isActive,
          accountname: solicitorForm.accountname,
        };

        await updateSolicitor(solicitorId, solicitorUpdateData);
        showSuccessDialog('募集人情報の編集が完了しました。');
      }
    } catch (err) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
    } finally {
      setIsLoading(false);
      setShowEditConfirmation(false);
    }
  };

  // ローディング中
  if (isLoading) return <div>Loading...</div>;

  // エラー発生、またはデータがない場合
  if (error || !agencyData) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <div className="error-actions">
          <>
            <button
              onClick={handleManualRetry}
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? '読み込み中...' : '再試行'}
            </button>
            <button
              onClick={() => navigate(-1)}
              className="btn btn-secondary"
            >
              前の画面に戻る
            </button>
          </>
        </div>
      </div>
    );
  }

  return (
    <>
      <AgencyHeader />
      <div className="page-container">
        <div className="content-wrapper">
          <div className="page-header">
            <div className="title-section">
              <div className="icon-square">
                <img src={iconTitle} alt="タイトルアイコン" />
              </div>
              <h1>代理店編集：{agencyData.name}</h1>
            </div>
            {activeTab === 'solicitors' && (
              <div className="solicitor-add-button-box">
                <button
                  className="solicitor-add-button button-small solicitor-add-button--pale-blue solicitor-add-button--img-none"
                  onClick={() => handleCsvUpload(agencyId)}
                >
                  <span>CSVアップロード</span>
                  <img src={iconBtnUl} alt="ボタンアイコン" className="solicitor-icon-img" />
                </button>
                <button
                  className="solicitor-add-button button-small solicitor-add-button--img-none solicitor-add-button--img-none"
                  onClick={() => handleCsvDownload(agencyId)}
                >
                  <span>CSVダウンロード</span>
                  <img src={iconBtnDl} alt="ボタンアイコン" className="solicitor-icon-img" />
                </button>
              </div>
            )}
          </div>

          <div className="tab-container">
            <div className="tab-menu">
              <button
                className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
                onClick={() => setActiveTab('info')}
              >
                代理店情報
              </button>
              <button
                className={`tab-button ${activeTab === 'insurance' ? 'active' : ''}`}
                onClick={() => setActiveTab('insurance')}
              >
                登録保険会社
              </button>
              <button
                className={`tab-button ${activeTab === 'solicitors' ? 'active' : ''}`}
                onClick={() => setActiveTab('solicitors')}
              >
                募集人
              </button>
            </div>

            <div className="tab-content">
              {activeTab === 'info' && (
                <AgencyInfoTab
                  formData={formData}
                  handleChange={e => setFormData({ ...formData, [e.target.name]: e.target.value })}
                  onConfirm={() => {
                    // 代理店バリデーション
                    const validationResult = validateAgencyForm(formData);
                    if (displayAgencyValidationErrorsWithDialog(validationResult, showError)) {
                      setShowEditConfirmation(true);
                    }
                  }}
                  onReturn={() => navigate(-1)}
                  documentTypes={documentTypes}
                  selectedDocumentTypeIds={formData.documentTypeIds}
                  onDocumentTypeToggle={handleDocumentTypeToggle}
                />
              )}

              {activeTab === 'insurance' && (
                <AgencyInsuranceTab
                  insuranceCompanies={insuranceCompanies}
                  selectedCompanies={selectedCompanies}
                  handleCheckboxChange={(id) => {
                    setSelectedCompanies(prev => {
                      return prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id];
                    });
                  }}
                  onConfirm={() => {
                    // 保険会社のバリデーション
                    let hasSelectedCompany = false;

                    if (selectedCompanies.length > 0) {
                      hasSelectedCompany = true;
                    }

                    if (!hasSelectedCompany) {
                      showError({
                        message: '少なくとも1つの保険会社を選択してください。',
                        error: null,
                        statusCode: 422
                      });
                      return;
                    }

                    setShowEditConfirmation(true);
                  }}
                  onReturn={() => navigate(-1)}
                />
              )}

              {activeTab === 'solicitors' && (
                solicitorId ? (
                  <AgencySolicitorsEditTab
                    solicitorForm={solicitorForm}
                    handleChange={e => setSolicitorForm({ ...solicitorForm, [e.target.name]: e.target.value })}
                    handleStatusButtonClick={handleStatusButtonClick}
                    handleEdit={() => setShowEditConfirmation(true)}
                    onReturn={() => navigate(-1)}
                  />
                ) : (
                  <AgencySolicitorsTab
                    agencyId={agencyId}
                    solicitors={solicitors}
                    onStatusChange={() => setShowStatusConfirmation(true)}
                    onReturn={() => navigate(-1)}
                    onAddSolicitor={() => navigate(`/agency/registration/${agencyId}`)}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 停止・再開確認ポップアップ */}
      <StatusConfirmationPopup
        showConfirmationPopup={showStatusConfirmation}
        confirmationPopupData={statusConfirmationData}
        closeConfirmationPopup={() => setShowStatusConfirmation(false)}
        confirmStatusChange={confirmStatusChange}
      />

      {/* 登録確認ポップアップ */}
      <AgencyRegistrationPop
        showConfirmationPopup={showConfirmationPopup}
        formData={activeTab === 'solicitors' ? solicitorForm : formData}
        closeConfirmationPopup={() => setShowConfirmationPopup(false)}
        confirmRegistration={confirmRegistration}
        activeTab={activeTab}
        documentTypes={documentTypes}
      />

      {/* 編集確認ポップアップ */}
      <AgencyEditConfirmationPop
        showConfirmationPopup={showEditConfirmation}
        formData={activeTab === 'solicitors' ? solicitorForm : formData}
        closeConfirmationPopup={() => setShowEditConfirmation(false)}
        confirmEdit={confirmEdit}
        activeTab={activeTab}
        selectedCompanies={selectedCompanies}
        insuranceCompanies={insuranceCompanies}
        documentTypes={documentTypes}
      />
    </>
  );
};

export default AgencyEdit;
